import { useContext } from "react"

import { useTrans, Trans } from "@/i18n"

import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { Button } from "@/components/Button"
import { RecipientStatusBanner } from "@/components/forms/RecipientStatusBanner"

import {
	FiCheckCircle,
	FiX,
	FiChevronRight,
	FiAlertTriangle,
} from "@/lib/icons"

import {
	MangopayScaConsentState,
	useUserContextQuery,
	useScaConsentManageMutation,
	useScaEnrollmentStartMutation,
	type ScaConsentManageMutation,
	type ScaEnrollmentStartMutation,
} from "@/api/graphql"

import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/useToasts"

import { captureMessage } from "@/lib/sentry"
import { cn } from "@/lib/utils"
import { EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL } from "@/lib/env"

type ConsentKey =
	| "sca_consent_contact_information"
	| "sca_consent_view_account_information"
	| "sca_consent_recipient_registration"
	| "sca_consent_transfer"

const CONSENT_ROWS: { key: ConsentKey; labelKey: string }[] = [
	{
		key: "sca_consent_transfer",
		labelKey: "investor:mangopay.consent.transfer",
	},
	{
		key: "sca_consent_contact_information",
		labelKey: "investor:mangopay.consent.contact_information",
	},
	{
		key: "sca_consent_view_account_information",
		labelKey: "investor:mangopay.consent.view_account_information",
	},
	{
		key: "sca_consent_recipient_registration",
		labelKey: "investor:mangopay.consent.recipient_registration",
	},
]

export function InvestorPayments() {
	const t = useTrans("investor")

	const { data, isLoading } = useUserContextQuery()

	const { redirect } = useContext(RedirectContext)
	const { addToast } = useToasts()

	const { mutate: startScaEnrollment, isPending: isEnrolling } =
		useScaEnrollmentStartMutation()
	const { mutate: manageScaConsent, isPending: isManaging } =
		useScaConsentManageMutation()

	if (isLoading) {
		return null
	}

	const scaData = data?.me?.mangopay_sca
	if (!scaData) {
		return null
	}

	// Only consider rows Mangopay has activated for our platform. NOT_APPLICABLE
	// rows are hidden and don't gate the alert or badge.
	const visibleRows = CONSENT_ROWS.filter(
		(row) => scaData[row.key] !== MangopayScaConsentState.NotApplicable,
	)
	const allConsentsGranted =
		scaData.sca_enrolled &&
		visibleRows.every(
			(row) => scaData[row.key] === MangopayScaConsentState.Active,
		)

	const showWalletUnavailableToast = () => {
		addToast({
			variant: "alert",
			id: "investor-payments-wallet-unavailable",
			text: t("investor:identity.copy.mangopay.error.wallet_unavailable"),
		})
	}

	const handleEnrollSuccess = (result: ScaEnrollmentStartMutation) => {
		const redirectUrl = result?.sca_enrollment_start?.redirect_url
		if (redirectUrl) {
			redirect(redirectUrl)
			return
		}
		captureMessage("User unable to enroll SCA from investor payments", {
			level: "error",
			extra: { reason: "missing_redirect_url", result },
		})
		showWalletUnavailableToast()
	}

	const handleEnrollError = (error: unknown) => {
		captureMessage("User unable to enroll SCA from investor payments", {
			level: "error",
			extra: { reason: "mutation_error", error },
		})
		showWalletUnavailableToast()
	}

	const handleManageSuccess = (result: ScaConsentManageMutation) => {
		// Mangopay requires SCA enrollment first; chain into the enrollment flow.
		if (result?.sca_consent_manage?.sca_required) {
			startScaEnrollment(
				{ return_url: window.location.href },
				{
					onSuccess: handleEnrollSuccess,
					onError: handleEnrollError,
				},
			)
			return
		}

		const redirectUrl = result?.sca_consent_manage?.redirect_url
		if (redirectUrl) {
			redirect(redirectUrl)
			return
		}

		captureMessage(
			"User unable to manage SCA consent from investor payments",
			{
				level: "error",
				extra: { reason: "missing_redirect_url", result },
			},
		)
		showWalletUnavailableToast()
	}

	const handleManageError = (error: unknown) => {
		captureMessage(
			"User unable to manage SCA consent from investor payments",
			{
				level: "error",
				extra: { reason: "mutation_error", error },
			},
		)
		showWalletUnavailableToast()
	}

	const handleManageClick = () => {
		manageScaConsent(
			{ return_url: window.location.href },
			{
				onSuccess: handleManageSuccess,
				onError: handleManageError,
			},
		)
	}

	return (
		<>
			<title>
				{t("common:web.title", {
					page: t("investor:mangopay.title"),
				})}
			</title>
			<CardWrapper>
				<CardBody>
					<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
						{t("investor:mangopay.title")}
					</Heading>

					<p className="mb-4 text-sm whitespace-break-spaces text-gray-600">
						{t("investor:mangopay.copy")}
					</p>

					<img
						src="/assets/partners/mangopay_logo.png"
						alt="Mangopay logo"
						className="mb-4 w-28"
					/>

					<ConsentStatusAlert allGranted={allConsentsGranted} t={t} />

					<div className="space-y-4">
						{/* Enrollment status */}
						<div className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-4">
							<div>
								<h3 className="text-sm font-medium text-gray-900">
									{t("investor:mangopay.enrollment.title")}
								</h3>
								<p className="mt-1 text-sm text-gray-600">
									{t(
										"investor:mangopay.enrollment.description",
									)}
								</p>
							</div>
							<StatusPill enabled={scaData.sca_enrolled} t={t} />
						</div>

						{/* Recipient status — reuse existing banner */}
						{scaData.recipient_status && (
							<RecipientStatusBanner
								status={scaData.recipient_status}
								maskedIban={
									data?.me?.investor?.masked_iban ?? null
								}
							/>
						)}

						{/* Consents. Mangopay controls which permissions its hosted
						    page renders; we can't scope the request per the schema
						    docs on sca_consent_manage. sca_consent_manage returns
						    sca_required=true when the user isn't enrolled yet, and
						    handleManageSuccess chains into enrollment automatically. */}
						{visibleRows.length > 0 && (
							<div className="rounded-md border border-gray-200">
								<div className="border-b border-gray-200 p-4">
									<h3 className="text-sm font-medium text-gray-900">
										{t("investor:mangopay.consent.title")}
									</h3>
									<p className="mt-1 text-sm text-gray-600">
										{t(
											"investor:mangopay.consent.description",
										)}
									</p>
								</div>
								<ul className="divide-y divide-gray-200">
									{visibleRows.map((row) => (
										<li
											key={row.key}
											className="flex items-center justify-between gap-4 p-4"
										>
											<span className="text-sm text-gray-700">
												{t(row.labelKey)}
											</span>
											<ConsentStatusPill
												state={scaData[row.key]}
												t={t}
											/>
										</li>
									))}
								</ul>
							</div>
						)}

						<div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
							<Button
								type="button"
								onClick={handleManageClick}
								disabled={isManaging || isEnrolling}
							>
								{t("investor:mangopay.consent.action")}
								<FiChevronRight className="ml-2" />
							</Button>
						</div>

						<p className="text-sm text-gray-600">
							<Trans
								i18nKey="investor:identity.copy.mangopay.copy.faq"
								components={{
									/* eslint-disable jsx-a11y/anchor-has-content */
									a: (
										<a
											className="text-secondary-700 inline-flex items-center font-medium"
											href={
												EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL
											}
											target="_blank"
											rel="noreferrer"
										/>
									),
									/* eslint-enable jsx-a11y/anchor-has-content */
								}}
							/>
						</p>
					</div>
				</CardBody>
			</CardWrapper>
		</>
	)
}

function ConsentStatusAlert({
	allGranted,
	t,
}: {
	allGranted: boolean
	t: (key: string) => string
}) {
	const config = allGranted
		? {
				container: "bg-green-50",
				titleClass: "text-green-800",
				bodyClass: "text-green-700",
				icon: <FiCheckCircle className="size-5 text-green-500" />,
				titleKey: "investor:mangopay.consent.alert.complete.title",
				bodyKey: "investor:mangopay.consent.alert.complete.body",
			}
		: {
				container: "bg-yellow-50",
				titleClass: "text-yellow-800",
				bodyClass: "text-yellow-700",
				icon: <FiAlertTriangle className="size-5 text-yellow-500" />,
				titleKey: "investor:mangopay.consent.alert.incomplete.title",
				bodyKey: "investor:mangopay.consent.alert.incomplete.body",
			}

	return (
		<div
			className={cn("mb-4 rounded-md p-4", config.container)}
			data-testid="mangopay-consent-alert"
			data-complete={allGranted}
		>
			<div className="flex items-start">
				<div className="shrink-0">{config.icon}</div>
				<div className="ml-3">
					<h3
						className={cn("text-sm font-medium", config.titleClass)}
					>
						{t(config.titleKey)}
					</h3>
					<p className={cn("mt-1 text-sm", config.bodyClass)}>
						{t(config.bodyKey)}
					</p>
				</div>
			</div>
		</div>
	)
}

function StatusPill({
	enabled,
	t,
}: {
	enabled: boolean
	t: (key: string) => string
}) {
	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
				enabled
					? "bg-green-50 text-green-800"
					: "bg-gray-100 text-gray-600",
			)}
			data-testid="mangopay-status-pill"
			data-enabled={enabled}
		>
			{enabled ? (
				<FiCheckCircle className="size-3.5" />
			) : (
				<FiX className="size-3.5" />
			)}
			{t(
				enabled
					? "investor:mangopay.status.enabled"
					: "investor:mangopay.status.disabled",
			)}
		</span>
	)
}

function ConsentStatusPill({
	state,
	t,
}: {
	state: MangopayScaConsentState
	t: (key: string) => string
}) {
	// NOT_APPLICABLE rows are filtered out before render; treat as INACTIVE for
	// safety if a caller does render one.
	const enabled = state === MangopayScaConsentState.Active
	return <StatusPill enabled={enabled} t={t} />
}
